/**
 * Magma Skin - Base Component Factory
 *
 * Creates magma-themed color-specific base components using the magma-specific
 * factory (gradient-based colors with volcanic obsidian frame). Does NOT use
 * createColoredBase from createBaseVariants — the magma SVG structure is
 * different from basics.
 */

import React from 'react';
import type { ColoredBaseProps } from '../../constants/types';
import MagmaBaseComponent from './MagmaBaseComponent';
import { MAGMA_COLOR_SCHEMES, type MagmaBaseColorScheme } from './constants/baseColorSchemes';
import { RedPawn, BluePawn, YellowPawn, GreenPawn } from '../../pawns/magma/pawnFactory';
import type { PawnProps } from '../../constants/types';

function createMagmaBase(
  color: string,
  colorScheme: MagmaBaseColorScheme,
  PawnComponent: React.ComponentType<PawnProps>
) {
  const displayName = `Magma${color.charAt(0).toUpperCase() + color.slice(1)}Base`;

  const Base = React.memo(function MagmaBase(props: ColoredBaseProps) {
    return (
      <MagmaBaseComponent
        {...props}
        colorScheme={colorScheme}
        PawnComponent={props.PawnComponent ?? PawnComponent}
      />
    );
  });

  Base.displayName = displayName;
  return Base;
}

export const RedBase = createMagmaBase('red', MAGMA_COLOR_SCHEMES.red, RedPawn);
export const BlueBase = createMagmaBase('blue', MAGMA_COLOR_SCHEMES.blue, BluePawn);
export const YellowBase = createMagmaBase('yellow', MAGMA_COLOR_SCHEMES.yellow, YellowPawn);
export const GreenBase = createMagmaBase('green', MAGMA_COLOR_SCHEMES.green, GreenPawn);

export type { ColoredBaseProps as BlueBaseProps };
export type { ColoredBaseProps as RedBaseProps };
export type { ColoredBaseProps as GreenBaseProps };
export type { ColoredBaseProps as YellowBaseProps };
